// File: client/src/components/layout/Footer.jsx
// Soft gradient footer with contact block + sticker-style trust badges.

import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaHeart,
  FaShieldAlt,
  FaLock,
  FaUserMd,
  FaSms,
  FaEnvelope,
  FaMapMarkerAlt
} from 'react-icons/fa';
import BRAND_CONFIG from '../../config/brand';

const Footer = () => {
  const year = new Date().getFullYear();
  const c = BRAND_CONFIG.contact;
  const a = c.address;

  return (
    <footer className="mt-20 bg-blob border-t border-primary-100">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-[#FF5CA8] to-[#8B2ABE] flex items-center justify-center shadow-md shadow-pink-300/40">
                <FaHeart className="text-white w-3.5 h-3.5" />
              </span>
              <span className="text-xl font-extrabold tracking-tight text-ink">
                {BRAND_CONFIG.name}
              </span>
            </div>
            <p className="text-sm text-ink/70 leading-relaxed">
              {BRAND_CONFIG.description}
            </p>
          </div>

          {/* Nav */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary-700 mb-3">
              Explore
            </p>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm font-medium text-ink/80 hover:text-primary-700">Home</Link></li>
              <li><Link to="/book-consultation" className="text-sm font-medium text-ink/80 hover:text-primary-700">Book Consultation</Link></li>
              <li><Link to="/privacy" className="text-sm font-medium text-ink/80 hover:text-primary-700">Privacy</Link></li>
              <li><Link to="/terms" className="text-sm font-medium text-ink/80 hover:text-primary-700">Terms</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary-700 mb-3">
              Contact
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`sms:${c.phoneGeneralDigits}`}
                  className="group flex items-start gap-2 text-ink/80 hover:text-primary-700"
                >
                  <FaSms className="mt-1 w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold block group-hover:text-primary-700">{c.phoneGeneral}</span>
                    <span className="text-xs text-ink/55">{c.phoneGeneralPurpose}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`sms:${c.phoneCaseDigits}`}
                  className="group flex items-start gap-2 text-ink/80 hover:text-primary-700"
                >
                  <FaSms className="mt-1 w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                  <span>
                    <span className="font-semibold block group-hover:text-primary-700">{c.phoneCase}</span>
                    <span className="text-xs text-ink/55">{c.phoneCasePurpose}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${c.supportEmail}`}
                  className="flex items-start gap-2 text-ink/80 hover:text-primary-700"
                >
                  <FaEnvelope className="mt-1 w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                  <span className="font-semibold">{c.supportEmail}</span>
                </a>
              </li>
              <li className="flex items-start gap-2 text-ink/80">
                <FaMapMarkerAlt className="mt-1 w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                <span className="text-xs leading-relaxed">
                  {a.street}<br />
                  {a.city}, {a.state} {a.zip}
                </span>
              </li>
            </ul>
          </div>

          {/* Trust badges */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary-700 mb-3">
              Trust &amp; Safety
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="sticker inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-ink">
                <FaShieldAlt className="text-emerald-500 w-3 h-3" /> HIPAA
              </span>
              <span className="sticker inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-ink">
                <FaUserMd className="text-primary-600 w-3 h-3" /> US-Licensed MDs
              </span>
              <span className="sticker inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-ink">
                <FaLock className="text-gray-600 w-3 h-3" /> Private
              </span>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-primary-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-ink/60">
            © {year} {BRAND_CONFIG.company.name}. All rights reserved.
          </p>
          <p className="text-xs text-ink/60 text-center sm:text-right">
            Medical services by US-licensed, board-certified physicians. Payments by Stripe.
          </p>
        </div>

        <div className="mt-4 space-y-2">
          <p className="text-[11px] text-ink/55 leading-relaxed">
            <span className="font-semibold">Part of the ChatWithDr Medical Group.</span>{' '}
            FemaleUTI is a focused brand operated by {BRAND_CONFIG.company.name}, the same medical
            group behind ChatWithDr. Some clinical emails — doctor messages, prescription
            confirmations, intake reminders — may arrive from ChatWithDr. That's the same US-licensed
            physicians treating your case.
          </p>
          <p className="text-[11px] text-ink/50 leading-relaxed">
            <span className="font-semibold">Emergency notice:</span> If you are experiencing a medical
            emergency, call 911 or go to your nearest emergency room immediately. This service is not
            for emergency situations.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
